import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { exerciseTypeIcon } from "../../../components/programBuilder/exerciseIcons";
import { EXERCISE_TYPES } from "../../../lib/programBuilderUtils";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
	describePrescriptionContent,
	summarizeResult,
} from "../lib/resultSummary";
import { ExerciseDetailAccordion } from "./ExerciseDetailAccordion";

/**
 * Una riga dello storico del Diario: un risultato registrato.
 *
 * Chiusa dice cosa è stato fatto (nome + riassunto del risultato); aperta
 * mostra il dettaglio, con la prescrizione accanto quando il risultato viene
 * da un programma.
 *
 * Props:
 *   - exercise: da `exerciseForResult` (dayAggregator), con `.result`
 *   - language: "it" | "en"
 *   - autoOpen: apre e porta in vista la riga (deep link `diaryFocus`)
 *   - onDelete: optional, mostra «Elimina risultato» nel dettaglio
 *   - onEdit: optional, mostra «Modifica» nel dettaglio
 */

function getExerciseDisplayName(exercise) {
	const defaultName = "Esercizio";
	if (!exercise || typeof exercise !== "object") return defaultName;

	if (exercise.exercise_type !== "conditioning") {
		return exercise.exercise_name || defaultName;
	}

	const rawName = String(exercise.exercise_name || "").trim();
	const condType = String(exercise.conditioning_type || "")
		.trim()
		.toLowerCase();

	if (!condType || condType === "free" || condType === "custom") {
		const cleaned = rawName
			.replace(/^Conditioning\s*-\s*/i, "")
			.replace(/\s*-\s*Custom\s*$/i, "")
			.replace(/^(Custom|Conditioning)$/i, "")
			.trim();
		return cleaned ? `Conditioning - ${cleaned}` : "Conditioning";
	}

	if (!rawName || rawName.toLowerCase() === "custom") return "Conditioning";
	return rawName;
}

export function ExerciseRow({
	exercise,
	language = "it",
	autoOpen = false,
	onDelete,
	onEdit,
}) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(false);
	const [isHighlighted, setIsHighlighted] = useState(false);
	const rowRef = useRef(null);
	const displayName = getExerciseDisplayName(exercise);
	const result = exercise.result;

	// Dry fire e live fire si somigliano nel nome ma sono due cose diverse: una
	// si fa a secco in casa, l'altra al poligono con le munizioni. Il badge lo
	// dice senza dover aprire la riga.
	const exerciseType = exercise.exercise_type;
	const isShooting =
		exerciseType === "dry_fire" || exerciseType === "live_fire";
	const shootingLabel = exerciseType === "dry_fire" ? "Dry fire" : "Live fire";
	const typeColor =
		EXERCISE_TYPES.find((type) => type.value === exerciseType)?.color ||
		"text-volt";
	const TypeIcon = exerciseTypeIcon(exerciseType);

	const summary = summarizeResult(result, exercise, language);
	// Il nome dice il tipo (For time, EMOM…); qui sotto cosa c'era da fare.
	// Un superset che si chiama già coi suoi movimenti non lo ripete.
	const prescriptionContent = describePrescriptionContent(exercise);
	const content =
		prescriptionContent && prescriptionContent !== displayName
			? prescriptionContent
			: "";
	const seenByCoach = Boolean(
		result?.coach_feedback_seen || result?.coach_feedback_seen_at,
	);

	useEffect(() => {
		if (!autoOpen) return;
		setOpen(true);
		setIsHighlighted(true);
		requestAnimationFrame(() => {
			const node = rowRef.current;
			if (!node) return;
			try {
				node.scrollIntoView({
					block: "center",
					behavior: "smooth",
				});
			} catch {
				node.scrollIntoView();
			}
		});
		const timer = setTimeout(() => setIsHighlighted(false), 2600);
		return () => clearTimeout(timer);
	}, [autoOpen]);

	return (
		<div
			ref={rowRef}
			className={`border-b border-border/40 last:border-b-0 transition-colors duration-500 ${
				isHighlighted ? "bg-volt/10 ring-1 ring-inset ring-volt/45" : ""
			}`}
			data-exercise-id={exercise?.exercise_id || ""}
		>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="w-full flex items-start gap-3 px-3 py-2.5 min-h-11 text-left hover:bg-white/[0.02] active:bg-white/[0.04] transition-colors"
				aria-expanded={open}
			>
				<TypeIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${typeColor}`} />
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-1.5 min-w-0">
						{isShooting && (
							<span
								className={`text-[10px] uppercase tracking-wider font-semibold shrink-0 ${typeColor}`}
							>
								{shootingLabel}
							</span>
						)}
						<span className="text-sm font-medium line-clamp-2 break-words text-foreground">
							{displayName}
						</span>
						{seenByCoach && (
							<span
								className="inline-flex shrink-0 text-volt"
								title={t("feedbackSeenByCoach")}
							>
								<Check className="w-3 h-3" />
							</span>
						)}
					</div>
					{content && (
						<div className="text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words">
							{content}
						</div>
					)}
					{summary && (
						<div className="text-xs text-volt mt-0.5 line-clamp-2 break-words">
							{summary}
						</div>
					)}
				</div>
				<div className="flex-shrink-0 mt-1 text-muted-foreground">
					{open ? (
						<ChevronDown className="w-4 h-4" />
					) : (
						<ChevronRight className="w-4 h-4" />
					)}
				</div>
			</button>
			<div
				className={`grid border-t border-border/30 bg-black/20 transition-[grid-template-rows,opacity] duration-200 ease-out ${
					open
						? "grid-rows-[1fr] opacity-100"
						: "grid-rows-[0fr] opacity-0 border-t-transparent"
				}`}
				aria-hidden={!open}
			>
				<div className="overflow-hidden">
					{open && (
						<>
							<ExerciseDetailAccordion exercise={exercise} language={language} />
							{(onEdit || onDelete) && (
								<div className="px-3 pb-3 flex justify-end gap-2">
									{onEdit && (
										<button
											type="button"
											onClick={onEdit}
											className="inline-flex items-center gap-1.5 min-h-11 px-2 text-xs text-muted-foreground hover:text-volt active:opacity-70"
										>
											<Pencil className="w-3.5 h-3.5" />
											{t("edit")}
										</button>
									)}
									{onDelete && (
									<button
										type="button"
										onClick={onDelete}
										className="inline-flex items-center gap-1.5 min-h-11 px-2 text-xs text-muted-foreground hover:text-red-400 active:opacity-70"
									>
										<Trash2 className="w-3.5 h-3.5" />
										{t("deleteResultTitle")}
									</button>
									)}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
